export const heroCopy = {
  headline: "The nervous system for your business.",
  headlineAccent: "FlowState.",
  subheadline:
    "FlowState Automate unifies your CRM, AI agents, tasks, finance, and calendar into one living network. Built on Google Cloud, designed for focus, and made for humans who think differently.",
  primaryCta: "Start free trial",
  primaryCtaHref: "/auth",
  secondaryCta: "Watch the network",
  secondaryCtaTarget: "#network",
  microCopy: "No credit card required · Runs on infrastructure you already trust · Cancel anytime",
} as const;

export const trustBar = {
  stats: [
    { label: "Google Cloud infrastructure", value: "$1.2T" },
    { label: "Edge functions ready", value: "50+" },
    { label: "Cognitive load reduced", value: "70%" },
    { label: "Uptime", value: "99.99%" },
  ],
  logos: [
    "Google Cloud",
    "Supabase Edge",
    "Cortex AI",
    "Mycelium Network",
    "FlowState",
  ],
} as const;

export const networkCopy = {
  badge: "How it works",
  headline: "The Mycelium Network",
  subheadline:
    "A mesh of intelligent edge functions, AI specialists, and Google Cloud services that route work, memory, and decisions to exactly where they need to go.",
  steps: [
    {
      title: "Senses",
      description:
        "Every click, task, message, and event is captured by Supabase Edge Functions close to the user.",
    },
    {
      title: "Thinks",
      description:
        "Cortex — the brain — routes the signal to the right AI specialist, memory, or human workflow.",
    },
    {
      title: "Acts",
      description:
        "Google Cloud runs the heavy lifting: inference, storage, search, and compute, without building new infrastructure from scratch.",
    },
  ],
} as const;

export const features = [
  {
    id: "ai-cortex",
    icon: "Bot",
    title: "Cortex AI Brain",
    description:
      "Cortex is the central nervous system. It understands context, recalls memory, and delegates work to specialist agents — 24/7.",
    highlights: ["Specialist workflows", "Natural language commands", "Federated memory"],
  },
  {
    id: "crm",
    icon: "Users",
    title: "CRM & Sales Pipeline",
    description:
      "Track leads, opportunities, and customer health in one pipeline. Integrations keep your sales motion in flow without tab-switching.",
    highlights: ["Pipeline & forecasting", "Contact timeline", "Sales portal"],
  },
  {
    id: "tasks",
    icon: "ListTodo",
    title: "Tasks & Projects",
    description:
      "Plan, assign, and ship work with smart task management and progress reports that reduce cognitive load for your whole team.",
    highlights: ["Kanban & lists", "Workflow automation", "Progress reports"],
  },
  {
    id: "accounting",
    icon: "Receipt",
    title: "Accounting & Invoicing",
    description:
      "Stay on top of transactions, subscriptions, and Xero sync without leaving the platform. One source of truth for your finances.",
    highlights: ["Xero integration", "Recurring billing", "Subscription tracking"],
  },
] as const;

export const metrics = [
  {
    value: "< 100ms",
    label: "Typical edge response",
    description: "Supabase Edge Functions run close to your users, everywhere.",
  },
  {
    value: "Zero",
    label: "New infra needed",
    description: "We run on Google Cloud and Supabase, so the planet doesn't pay for duplicate datacenters.",
  },
  {
    value: "24/7",
    label: "Agent runtime",
    description: "Cortex specialists keep working while your team sleeps.",
  },
  {
    value: "SOC 2",
    label: "Security aligned",
    description: "Role-based access, audit logs, and encrypted storage.",
  },
] as const;

export const missionCopy = {
  badge: "Built for focus",
  headline: "Technology that respects your brain",
  subheadline:
    "FlowState was built by someone who struggles with context switching. By bringing everything into one calm, predictable surface, we reduce the cognitive load that makes work exhausting for ADHD and neurodivergent minds.",
  points: [
    "One workspace. One notification surface. One source of truth.",
    "AI handles the remembering, so you stay in flow.",
    "Holographic design reduces visual noise and decision fatigue.",
  ],
  secondaryHeadline: "Sustainable scale",
  secondarySubheadline:
    "Why build more datacenters when Google already did? FlowState runs on the world's most efficient, carbon-intelligent cloud infrastructure — turning idle capacity into your competitive advantage.",
} as const;

export const finalCta = {
  headline: "Ready to wire your business into flow?",
  subheadline: "Join early-access teams using FlowState Automate to replace context switching with intelligent, beautiful automation.",
  cta: "Start free trial",
  ctaHref: "/auth",
} as const;

import type { SimpleIconSlug } from "../components/BrandIcon";

export interface BrandData {
  name: string;
  slug?: SimpleIconSlug;
  icon?: "Zap" | "Sparkles" | "Rocket" | "Code2";
  iconColor: string;
}

export const partners: BrandData[] = [
  { name: "Supabase", slug: "supabase", iconColor: "#3ECF8E" },
  { name: "TanStack", slug: "reactquery", iconColor: "#EF4444" },
  { name: "Electric SQL", icon: "Zap", iconColor: "#FACC15" },
  { name: "OpenRouter", slug: "openrouter", iconColor: "#4F46E5" },
  { name: "Google Cloud", slug: "googlecloud", iconColor: "#4285F4" },
  { name: "Vercel", slug: "vercel", iconColor: "#FFFFFF" },
];

export const builtWith: BrandData[] = [
  { name: "Cursor", slug: "cursor", iconColor: "#FFFFFF" },
  { name: "GitHub Copilot", slug: "githubcopilot", iconColor: "#FFFFFF" },
  { name: "Devin | Windsurf", iconColor: "#A855F7" },
  { name: "Antigravity", icon: "Rocket", iconColor: "#06B6D4" },
  { name: "VS Code", icon: "Code2", iconColor: "#3B82F6" },
  { name: "Framer Motion", slug: "framer", iconColor: "#0055FF" },
];

export const openSourceProjects: BrandData[] = [
  { name: "React", slug: "react", iconColor: "#61DAFB" },
  { name: "TanStack Router", slug: "reactrouter", iconColor: "#EF4444" },
  { name: "TanStack Query", slug: "reactquery", iconColor: "#EF4444" },
  { name: "Radix UI", slug: "radixui", iconColor: "#FFFFFF" },
  { name: "Tailwind CSS", slug: "tailwindcss", iconColor: "#38BDF8" },
  { name: "Framer Motion", slug: "framer", iconColor: "#0055FF" },
  { name: "Three.js", slug: "threedotjs", iconColor: "#FFFFFF" },
  { name: "Lucide", slug: "lucide", iconColor: "#F56565" },
  { name: "Supabase", slug: "supabase", iconColor: "#3ECF8E" },
  { name: "PostgreSQL", slug: "postgresql", iconColor: "#336791" },
  { name: "GitHub", slug: "github", iconColor: "#FFFFFF" },
  { name: "shadcn/ui", slug: "shadcnui", iconColor: "#FFFFFF" },
];

export const openSourceCopy = {
  badge: "Open source",
  headline: "Massive shoutout to the open source community",
  subheadline:
    "FlowState is built on the shoulders of giants. Every animation, component, database, and framework you see is the result of thousands of maintainers, contributors, and dreamers who give their work to the world. Thank you.",
  disclaimer:
    "All product names, logos, and brands are property of their respective owners. Use of these names, logos, and brands does not imply endorsement.",
} as const;

export const navLinks = [
  { label: "Features", href: "#features" },
  { label: "Network", href: "#network" },
  { label: "Mission", href: "#mission" },
  { label: "Metrics", href: "#metrics" },
] as const;
